import { Alert, Badge, Button, Grid, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconArrowRight, IconLock, IconUser, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  DEMO_CATALOG_COPY,
  DEMO_SCENARIOS,
  DEMO_SCENARIO_LABELS,
  type DemoScenario,
  type GameDetail,
} from '@creativitas/shared';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { AssetImage } from '@/components/ui/AssetImage';
import { PageHeader } from '@/components/ui/PageHeader';
import { lockedCopy } from '@/features/content/accessCopy';
import { toUserMessage } from '@/lib/api';
import {
  createGameSession,
  fetchGameSessions,
  GAME_SESSIONS_KEY,
  gameSessionKey,
} from '../api';
import { GameRulesButton } from '../GameRulesButton';

const SCENARIO_ICONS: Record<DemoScenario, React.ReactNode> = {
  solo: <IconUser size={18} />,
  facilitator: <IconUsers size={18} />,
};

/**
 * Страница демоверсии (ТЗ демо, пп. 2.1, 4).
 *
 * Сначала — что это: три сектора из шести, настоящие карты. Затем один вопрос:
 * как познакомиться с игрой. Выбор делается на каждую новую партию, а уже
 * начатые партии видны ниже: незавершённую можно продолжить, завершённую —
 * открыть на итоге. Новая партия начинается только отдельным нажатием.
 */
export function DemoGameDetail({ game }: { game: GameDetail }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locked = lockedCopy(game.access);
  const demo = game.demo;
  const sessions = useQuery({ queryKey: GAME_SESSIONS_KEY, queryFn: fetchGameSessions });
  const own = (sessions.data?.items ?? []).filter((item) => item.gameId === game.id);

  const create = useMutation({
    mutationFn: (demoScenario: DemoScenario) => createGameSession(game.slug, { demoScenario }),
    onSuccess: (session) => {
      queryClient.setQueryData(gameSessionKey(session.id), session);
      void queryClient.invalidateQueries({ queryKey: GAME_SESSIONS_KEY });
      void navigate(`/games/${session.game.slug}/session/${session.id}`);
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  const sectorTitles = (demo?.routeSectorIds ?? [])
    .map((id) => game.board?.sectors.find((sector) => sector.id === id)?.title ?? id);

  return (
    <>
      <PageHeader
        eyebrow="Игры"
        title={game.title}
        description={game.description}
        actions={<Button component={Link} to="/games" variant="subtle" leftSection={<IconArrowLeft size={16} />}>К списку</Button>}
      />
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder style={{ overflow: 'hidden' }}>
            <AssetImage src={game.coverImage} alt={game.title} ratio="1 / 1" fallbackLabel="Игровое поле" />
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="lg">
            <Stack gap="xs">
              <Group gap="xs">
                <Badge color="gold">{DEMO_CATALOG_COPY.badge}</Badge>
                {demo ? (
                  <Badge variant="light" data-testid="demo-sector-count">
                    {DEMO_CATALOG_COPY.sectors(demo.routeSectorIds.length, demo.totalSectors)}
                  </Badge>
                ) : null}
              </Group>
              <Text>
                Часть настоящей игры: поле, кубик, карты и ресурсы. Открыты сектора{' '}
                {sectorTitles.join(' → ')}. Остальные три ждут в полной версии.
              </Text>
              <Group>
                <GameRulesButton gameSlug={game.slug} label="Инструкция для демо" />
              </Group>
            </Stack>

            {demo?.fullGame?.accessible ? (
              <Alert color="teal" variant="light" title="Полная игра у вас уже есть">
                <Group justify="space-between" gap="sm">
                  <Text size="sm">Демо можно пройти для знакомства — или сразу перейти к полной игре.</Text>
                  <Button
                    size="xs"
                    component={Link}
                    to={`/games/${demo.fullGame.slug}`}
                    rightSection={<IconArrowRight size={14} />}
                  >
                    К полной игре
                  </Button>
                </Group>
              </Alert>
            ) : null}

            {locked ? (
              <Alert color="gold" icon={<IconLock size={18} />} title={locked.reason}>{locked.hint}</Alert>
            ) : (
              <Stack gap="sm">
                <Title order={3}>{DEMO_CATALOG_COPY.question}</Title>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  {DEMO_SCENARIOS.map((scenario) => {
                    const copy = DEMO_CATALOG_COPY.scenarios[scenario];
                    return (
                      <Paper key={scenario} withBorder p="md" data-testid={`demo-scenario-${scenario}`}>
                        <Stack gap="sm" h="100%" justify="space-between">
                          <Stack gap={6}>
                            <Group gap="xs" wrap="nowrap">
                              {SCENARIO_ICONS[scenario]}
                              <Text fw={700}>{copy.title}</Text>
                            </Group>
                            <Text size="sm" c="var(--text-muted)">{copy.body}</Text>
                          </Stack>
                          <Button
                            leftSection={SCENARIO_ICONS[scenario]}
                            loading={create.isPending && create.variables === scenario}
                            disabled={create.isPending}
                            onClick={() => create.mutate(scenario)}
                          >
                            {copy.action}
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </Stack>
            )}

            {own.length > 0 ? (
              <Stack gap="xs" data-testid="demo-sessions">
                <Title order={4}>Ваши демопартии</Title>
                {own.map((item) => {
                  const done = item.status === 'completed';
                  return (
                    <Paper key={item.id} withBorder p="sm">
                      <Group justify="space-between" gap="sm">
                        <Group gap="xs">
                          {item.demoScenario ? (
                            <Badge variant="light">{DEMO_SCENARIO_LABELS[item.demoScenario]}</Badge>
                          ) : null}
                          <SessionStatusBadge status={item.status} />
                          <Text size="xs" c="var(--text-muted)">
                            {new Date(item.lastActivityAt).toLocaleString('ru-RU', {
                              day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
                            })}
                          </Text>
                        </Group>
                        <Button
                          size="xs"
                          variant={done ? 'light' : 'filled'}
                          component={Link}
                          to={`/games/${item.gameSlug}/session/${item.id}`}
                        >
                          {done ? 'Посмотреть итог' : 'Продолжить'}
                        </Button>
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            ) : null}
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
}
